// Library Imports
import axios from 'axios';

// Local Imports
import endPoints from '@/redux/constant';
import { store } from '@/redux/store';

export const USER_EVENTS = {
  LOGIN: 'Logged In',
  SIGNUP: 'Direct Media Kit Signup',
  LOGOUT: 'Logged Out',
  PURCHASE_SUCCESS: 'Completed Purchase',
  SUBSCRIPTION_CANCELLED: 'Cancelled Subscription',
  SAMPLE_KIT_CONVERTED_TO_REAL: 'Converted Sample Kit to Real',
  MK_PUBLISHED: 'Published Media Kit',
  MK_THEME_UPDATED: 'Updated Theme Color',
  MK_EMAIL_UPDATED: 'Updated Email',
  MK_FAV_FACTS_UPDATED: 'Updated Favourite Facts',
  MK_TOP_INTEREST_UPDATED: 'Updated Top Interests',
  MK_DELIVERABLES_UPDATED: 'Updated Deliverables',
  MK_RATES_UPDATED: 'Updated Rates',
  MK_PROFILE_NAME_UPDATED: 'Updated Profile Name',
  MK_AGE_RANGE_UPDATED: 'Updated Age Range',
  MK_LOCATION_UPDATED: 'Updated Location',
  MK_TOP_VIDEOS_UPDATED: 'Updated Top Videos',
  MK_CTA_BANNER_UPDATED: 'Updated Collaboration Banner',
  MK_BRAND_PARTNERS_UPDATED: 'Updated Brand Partners',
  MK_LINKS_UPDATED: 'Updated Sales Links',
  MK_PROFILE_PHOTO_UPDATED: 'Updated Profile Photo',
  WP_MEDIA_KIT_CLAIMED: 'Claimed Media Kit via WordPress',
  SAMPLE_KIT_LOADED: 'Sample Media Kit Loaded',
  SAMPLE_KIT_LOAD_FAILED: 'Sample Media Kit Load Failed',
  MK_EDIT_OPENED: 'Opened Edit Media Kit',
  MK_PREVIEW_CLICKED: 'Clicked Preview',
  MK_SHAREABLE_LINK_CLICKED: 'Clicked Get Shareable Link',
  MK_UPSELL_SHOWN_VERSION_A: 'Upsell Popup Shown (Version A)',
  MK_UPSELL_SHOWN_VERSION_B: 'Upsell Popup Shown (Version B)',
  MK_UPSELL_CONTINUE_VERSION_A: 'Upsell Continue Clicked (Version A)',
  MK_UPSELL_CONTINUE_VERSION_B: 'Upsell Continue Clicked (Version B)',
  MK_UPSELL_CLOSED_VERSION_A: 'Upsell Popup Closed (Version A)',
  MK_UPSELL_CLOSED_VERSION_B: 'Upsell Popup Closed (Version B)',
  PAYMENT_MODAL_OPENED: 'Payment Modal Opened',
  CHECKOUT_FORM_STARTED: 'Started Checkout Form',
  CHECKOUT_BLOCKED: 'Checkout Blocked',
  PAYMENT_FAILED: 'Payment Failed',
  SET_PASSWORD_MODAL_OPENED: 'Set Password Modal Opened',
  SET_PASSWORD_MODAL_CLOSED: 'Set Password Modal Closed',
  PASSWORD_SET: 'Password Set',
  MK_PROFILE_PHOTO_OPENED: 'Opened Profile Photo',
  MK_EMAIL_OPENED: 'Opened Email',
  MK_FAV_FACTS_OPENED: 'Opened Favourite Facts',
  MK_TOP_INTEREST_OPENED: 'Opened Top Interests',
  MK_DELIVERABLES_OPENED: 'Opened Deliverables',
  MK_RATES_OPENED: 'Opened Rates',
  MK_PROFILE_NAME_OPENED: 'Opened Profile Name',
  MK_AGE_RANGE_OPENED: 'Opened Age Range',
  MK_LOCATION_OPENED: 'Opened Location',
  MK_TOP_VIDEOS_OPENED: 'Opened Top Videos',
  MK_CTA_BANNER_OPENED: 'Opened Collaboration Banner',
  MK_BRAND_PARTNERS_OPENED: 'Opened Brand Partners',
  MK_LINKS_OPENED: 'Opened Sales Links',
  MK_SOCIALS_OPENED: 'Opened Socials',
  MK_BIO_OPENED: 'Opened Bio',
  MK_SOCIAL_CHANNELS_OPENED: 'Opened Social Channels',
  MK_TOP_CONTENT_1_OPENED: 'Opened Top Content 1',
  MK_TOP_CONTENT_2_OPENED: 'Opened Top Content 2',
  MK_TOP_POST_OPENED: 'Opened Top Post',
  MK_ADD_TOP_POST_OPENED: 'Opened Add Top Post',
  MK_BRAND_REVIEWS_OPENED: 'Opened Brand Reviews',
  MK_BIO_UPDATED: 'Updated Bio',
  MK_BIO_VISIBILITY_UPDATED: 'Updated Bio Visibility',
  MK_TOP_POSTS_VISIBILITY_UPDATED: 'Updated Top Posts Visibility',
  MK_TOP_CONTENT_1_VISIBILITY_UPDATED: 'Updated Top Content 1 Visibility',
  MK_TOP_CONTENT_2_VISIBILITY_UPDATED: 'Updated Top Content 2 Visibility',
  MK_BANNER_VISIBILITY_UPDATED: 'Updated Verification Banner Visibility',
  MK_AUDIENCE_VISIBILITY_UPDATED: 'Updated Audience Visibility',
  MK_BRAND_REVIEWS_VISIBILITY_UPDATED: 'Updated Brand Reviews Visibility',
  MK_BRAND_REVIEW_REQUESTED: 'Requested Brand Review',
  MK_TOP_POST_SUBMITTED: 'Submitted Top Post',
};

// Visit rules mirror the admin view (panels/Admin/Users/UserDetail/deriveActivity.js):
// a visit starts on signup / WP claim / login, or after 15 minutes with no activity.
const VISIT_STORAGE_KEY = 'mkTrackingVisit';
const VISIT_IDLE_MS = 15 * 60 * 1000;
const VISIT_START_EVENTS = new Set([USER_EVENTS.SIGNUP, USER_EVENTS.WP_MEDIA_KIT_CLAIMED, USER_EVENTS.LOGIN]);

// The upsell version the user clicked Continue on is carried into these checkout events' detail
const UPSELL_CONTINUE_VERSIONS = {
  [USER_EVENTS.MK_UPSELL_CONTINUE_VERSION_A]: 'Version A',
  [USER_EVENTS.MK_UPSELL_CONTINUE_VERSION_B]: 'Version B',
};
const CHECKOUT_EVENTS = new Set([
  USER_EVENTS.PAYMENT_MODAL_OPENED,
  USER_EVENTS.CHECKOUT_FORM_STARTED,
  USER_EVENTS.CHECKOUT_BLOCKED,
  USER_EVENTS.PAYMENT_FAILED,
  USER_EVENTS.PURCHASE_SUCCESS,
]);

// Fallback when localStorage is unavailable (private mode, blocked storage)
let memoryVisit = null;

const readVisit = () => {
  try {
    return JSON.parse(localStorage.getItem(VISIT_STORAGE_KEY)) || memoryVisit;
  } catch {
    return memoryVisit;
  }
};

const writeVisit = visit => {
  memoryVisit = visit;
  try {
    localStorage.setItem(VISIT_STORAGE_KEY, JSON.stringify(visit));
  } catch { /* ignore */ }
};

const resolveVisit = (userId, eventName) => {
  const now = Date.now();
  const visit = readVisit();
  const isOtherUser = Boolean(userId && visit?.userId && visit.userId !== userId);
  const isExpired = !visit || isOtherUser || now - visit.lastActivityAt > VISIT_IDLE_MS;

  if (isExpired || VISIT_START_EVENTS.has(eventName)) {
    return { userId, lastActivityAt: now, trackedKeys: [] };
  }
  return { ...visit, userId: visit.userId || userId, lastActivityAt: now };
};

// Always resolves (never rejects), so callers can await it before a redirect.
const sendUserEvent = (specialTextName_chr, userIdOverride, extra, dedupKey) => {
  if (!specialTextName_chr) return Promise.resolve();

  const currentUser = store.getState()?.currentUser?.currentUser;
  if (currentUser?.planType === 'paid') return Promise.resolve();

  const userId = userIdOverride ?? currentUser?.userId;
  const userIdStr = userId ? String(userId) : '';

  const visit = resolveVisit(userIdStr, specialTextName_chr);
  const isDuplicate = Boolean(dedupKey && visit.trackedKeys.includes(dedupKey));
  if (dedupKey && !isDuplicate) visit.trackedKeys.push(dedupKey);
  if (UPSELL_CONTINUE_VERSIONS[specialTextName_chr]) visit.upsellVersion = UPSELL_CONTINUE_VERSIONS[specialTextName_chr];
  writeVisit(visit);

  if (isDuplicate) return Promise.resolve();

  const detail = CHECKOUT_EVENTS.has(specialTextName_chr)
    ? [visit.upsellVersion, extra?.popUpName_chr].filter(Boolean).join(' - ')
    : extra?.popUpName_chr || '';

  return axios.post(endPoints.postUserEvent, {
    userId_chr: userIdStr,
    pageRouteName_chr: extra?.pageRouteName_chr || '',
    popUpName_chr: detail,
    specialTextName_chr,
  }).catch(() => { });
};

// Fire-and-forget by default; await the returned promise when a redirect follows.
export const trackUserEvent = (specialTextName_chr, userIdOverride, extra) =>
  sendUserEvent(specialTextName_chr, userIdOverride, extra);

// Sends the event only the first time per visit. dedupKey defaults to the event name.
export const trackUserEventOncePerVisit = (specialTextName_chr, userIdOverride, extra, dedupKey = specialTextName_chr) =>
  sendUserEvent(specialTextName_chr, userIdOverride, extra, dedupKey);
